import React from "react"
import { useTranslation } from "react-i18next"
import { Analytics } from "@vercel/analytics/react"
import { Header, Footer } from "./LandingPage"
import SEO from "./common/SEO"

interface DeviceCardProps {
  brand: string;
  models: string[];
}

const tints = ['panel-tint-blue', 'panel-tint-lavender', 'panel-tint-mint']

const DeviceCard: React.FC<DeviceCardProps & { index: number }> = ({ brand, models, index }) => {
  return (
    <div className={`${tints[index % tints.length]} rounded-[2rem] p-6`}>
      <h3 className="text-xl font-bold mb-4">{brand}</h3>
      <ul className="space-y-2">
        {models.map((model, i) => (
          <li key={i} className="text-muted-foreground">{model}</li>
        ))}
      </ul>
    </div>
  )
}

const CompatibilityPage: React.FC = () => {
  const { t } = useTranslation();
  const compatibleDevices: DeviceCardProps[] = [
    {
      brand: "Shearwater",
      models: [
        "Peregrine",
        "Peregrine TX",
        "Petrel",
        "Petrel 2",
        "Petrel 3",
        "Perdix",
        "Perdix AI",
        "Perdix 2",
        "Perdix 3",
        "Teric",
        "Tern",
        "NERD 2"
      ]
    },
    {
      brand: "Suunto",
      models: [
        "Ocean",
        "Nautic",
        "Nautic S",
        "EON Steel",
        "EON Steel Black",
        "EON Core",
        "D5"
      ]
    },
    {
      brand: "Scubapro / Uwatec",
      models: [
        "G2",
        "G2 TEK",
        "G2 HUD",
        "G3",
        "Aladin A1",
        "Aladin A2",
        "Luna 2.0",
        "Luna 2.0 AI"
      ]
    },
    {
      brand: "Aqua Lung / Oceanic / Sherwood",
      models: [
        "Aqualung i770R",
        "Aqualung i550C",
        "Aqualung i330R",
        "Aqualung i300C",
        "Aqualung i200C",
        "Oceanic Geo 4.0",
        "Oceanic Veo 4.0",
        "Oceanic Pro Plus 4",
        "Oceanic Atom 3.1",
        "Sherwood Wisdom 3",
        "Sherwood Sage",
        "Apeks DSX"
      ]
    },
    {
      brand: "Mares",
      models: [
        "Icon HD",
        "Puck Pro",
        "Puck 4",
        "Smart",
        "Smart Air",
        "Quad",
        "Quad Air",
        "Genius"
      ]
    },
    {
      brand: "Cressi",
      models: [
        "Goa",
        "Cartesio",
        "Leonardo 2.0",
        "Donatello"
      ]
    },
    {
      brand: "Heinrichs Weikamp",
      models: [
        "OSTC 2",
        "OSTC 2 TR",
        "OSTC 3",
        "OSTC 4",
        "OSTC Plus",
        "OSTC Sport"
      ]
    },
    {
      brand: "Divesoft",
      models: [
        "Freedom",
        "Liberty"
      ]
    },
    {
      brand: "Ratio / Dive System",
      models: [
        "iDive Easy",
        "iDive Free",
        "iDive Deep",
        "iDive 2 Easy",
        "iDive 2 Free",
        "iDive 2 Deep",
        "iDive Color Easy",
        "iDive Color Free",
        "iDive Color Deep"
      ]
    },
    {
      brand: "Halcyon",
      models: [
        "Symbios HUD",
        "Symbios Handset"
      ]
    },
    {
      brand: "Also Supported",
      models: [
        "Deep Six Excursion",
        "Deepblu Cosmiq+",
        "Oceans S1",
        "McLean Extreme",
        "Seac Tablet"
      ]
    }
  ];

  return (
    <div className="flex min-h-screen flex-col relative">
      <SEO path="/compatibility" />
      <Header />
      <main className="flex-1">
        <section className="px-3 pt-3">
          <div className="panel-hero rounded-[2.5rem]">
          <div className="container flex flex-col items-center justify-center space-y-5 pt-40 md:pt-48 pb-16 text-center">
            <h1 className="text-4xl md:text-6xl font-black tracking-tighter leading-[0.95]">
              {t('compatibility.hero.title')}
            </h1>
            <p className="max-w-[600px] text-lg md:text-xl text-muted-foreground">
              {t('compatibility.hero.description')}{' '}
              {t('compatibility.hero.noDeviceQuestion')} <a href="mailto:hello@deepsealabs.co" className="text-[#1F80FF] hover:text-[#1F80FF]/80">{t('compatibility.hero.letUsKnow')}</a>
            </p>
          </div>
          </div>
        </section>

        <section className="container py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-[1200px] mx-auto">
            {compatibleDevices.map((device, index) => (
              <DeviceCard key={index} {...device} index={index} />
            ))}
          </div>
        </section>
      </main>
      <Footer />
      <Analytics />
    </div>
  )
}

export default CompatibilityPage